// src/App.js

import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";

// Global State Provider for Authentication
import { AuthProvider } from "./context/AuthContext";
import ScrollDownArrow from './components/shared/ScrollDownArrow';

// Components
import Navbar from "./components/Navbar";
import ProtectedRoute from './components/ProtectedRoute';

// Public Pages
import Homepage from './pages/Homepage';
import Services from './pages/Services';
import Authors from './pages/Authors';
import NotFound from './pages/NotFound';
import ThankYou from './pages/ThankYou';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelledPage from './pages/PaymentCancelledPage';
import Community from './pages/Community';
import DirectoryPage from './pages/DirectoryPage';
import ProfileDetailPage from './pages/ProfileDetailPage';
import SelfPublishingService from './pages/SelfPublishingService';
import Course from './pages/Course';
import Corporate from "./pages/Corporate";
import MeetSajeda from "./pages/MeetSajeda"; 

// --- YENİ EKLENEN WORKSHOP SAYFALARI ---
import Workshop1 from "./pages/Workshop1";
import Workshop2 from "./pages/Workshop2";
import Workshop3 from "./pages/Workshop3";
// ----------------------------------------

// Membership Pages
import LoginPage from './membership/LoginPage';
import SignupPage from './membership/SubscriptionPage';
import ProfilePage from "./membership/ProfilePage";

// Admin Pages
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import AdminLogin from './admin/Login';
import ManageEvents from './admin/ManageEvents';
import ManageProfessionals from './admin/ManageProfessionals';
import AddEventForm from "./admin/AddEventForm";
import ManageCampaigns from "./admin/ManageCampaigns";
import CreateCampaign from "./admin/CreateCampaign";
import StatisticsPage from './admin/StatisticsPage';
import ManageOrdersPage from './admin/ManageOrdersPage';
import TaskBoard from './admin/manage-tasks/TaskBoard';

// Add these imports at the top
import ManageBookPages from './admin/ManageBookPages';
import BookLandingPage from './pages/BookLandingPage';

// ADD THESE NEW IMPORTS for the user management pages
import UserListPage from './admin/manage-users/UserListPage';
import EditUserPage from './admin/manage-users/EditUserPage';
import AddUserPage from './admin/manage-users/AddUserPage'; 

import ContactPage from './pages/ContactPage';
import ManageMessages from './admin/ManageMessages';

const AppLayout = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  if (isAdminRoute) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />

        {/* --- ROLE-BASED ROUTE PROTECTION IS NOW IMPLEMENTED --- */}
        <Route 
          path="/admin"
          element={
            // This parent route protects the entire admin layout.
            // Only users with a staff role can even see the admin sidebar/navbar.
            <ProtectedRoute allowedRoles={['admin', 'editor', 'marketing', 'order']}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          {/* Routes nested inside AdminLayout */}
          <Route index element={<AdminDashboard />} /> {/* /admin path, visible to all staff */}
          
          {/* Editor Role Routes */}
          <Route path="manage-events" element={<ProtectedRoute allowedRoles={['admin', 'editor']}><ManageEvents /></ProtectedRoute>} />
          <Route path="add-event" element={<ProtectedRoute allowedRoles={['admin', 'editor']}><AddEventForm /></ProtectedRoute>} />
          
          {/* Marketing Role Routes */}
          <Route path="manage-professionals" element={<ProtectedRoute allowedRoles={['admin', 'marketing']}><ManageProfessionals /></ProtectedRoute>} />
          <Route path="manage-campaigns" element={<ProtectedRoute allowedRoles={['admin', 'marketing']}><ManageCampaigns /></ProtectedRoute>} />
          <Route path="create-campaign" element={<ProtectedRoute allowedRoles={['admin', 'marketing']}><CreateCampaign /></ProtectedRoute>} />
          <Route path="manage-messages" element={<ProtectedRoute allowedRoles={['admin', 'marketing']}><ManageMessages /></ProtectedRoute>} />
          
          {/* Order Role Route */}
          <Route path="manage-orders" element={<ProtectedRoute allowedRoles={['admin', 'order']}><ManageOrdersPage /></ProtectedRoute>} />
          <Route path="manage-tasks" element={<ProtectedRoute allowedRoles={['admin', 'editor', 'marketing', 'order']}><TaskBoard /></ProtectedRoute>} />
          
          {/* Admin-Only Route */}
          <Route path="statistics" element={<ProtectedRoute allowedRoles={['admin']}><StatisticsPage /></ProtectedRoute>} /> 
          <Route path="manage-users" element={<ProtectedRoute allowedRoles={['admin']}><UserListPage /></ProtectedRoute>} />
          <Route path="manage-users/edit/:userId" element={<ProtectedRoute allowedRoles={['admin']}><EditUserPage /></ProtectedRoute>} />
          <Route path="manage-users/add" element={<ProtectedRoute allowedRoles={['admin']}><AddUserPage /></ProtectedRoute>} />
          <Route 
            path="manage-book-pages" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'editor']}>
                <ManageBookPages />
              </ProtectedRoute>
            } 
          />
        </Route>
      </Routes>
    );
  }

  // --- No changes to the public-facing part of your app ---
  // Yeni workshop sayfaları buraya eklendi
  const isFullWidthPage = [
    "/", "/community", "/self-publishing-service", "/directory", "/contact", "/course", "/corporate", "/meet-sajeda", "/workshop-1", "/workshop-2", "/workshop-3"
  ].includes(location.pathname) || location.pathname.startsWith("/book/");
  
  const hasOwnContainer = ["/login", "/signup"].includes(location.pathname);
  const mainClassName = isFullWidthPage || hasOwnContainer ? "" : "mt-4 container";

  return (
    <div className="d-flex flex-column min-vh-100">
      <Navbar />
      <main className={`flex-grow-1 ${mainClassName}`}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Homepage />} />
          <Route path="/services" element={<Services />} />
          <Route path="/authors/*" element={<Authors />} />
          <Route path="/community" element={<Community />} />
          <Route path="/corporate" element={<Corporate />} />
          <Route path="/directory/:profileId" element={<ProfileDetailPage />} />
          <Route path="/directory" element={<DirectoryPage />} />
          <Route path="/self-publishing-service" element={<SelfPublishingService />} />
          <Route path="/meet-sajeda" element={<MeetSajeda />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/cancelled" element={<PaymentCancelledPage />} />
          <Route path="/contact" element={<ContactPage />} /> 
          <Route path="/course" element={<Course />} />

          {/* YENİ EKLENEN WORKSHOP YÖNLENDİRMELERİ */}
          <Route path="/workshop-1" element={<Workshop1 />} />
          <Route path="/workshop-2" element={<Workshop2 />} />
          <Route path="/workshop-3" element={<Workshop3 />} />
          {/* ---------------------------------------- */}
          
          {/* Membership Routes */}
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/book/:bookId" element={<BookLandingPage />} />
          
          {/* Catch-all Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <ScrollDownArrow /> 
      {/* <Footer /> */}
    </div>
  );
};

const App = () => (
  <HelmetProvider>
    <AuthProvider>
      <Router>
        <AppLayout />
      </Router>
    </AuthProvider>
  </HelmetProvider>
);

export default App;